import {Record, List, Iterable} from 'immutable';
import {UserConditionType} from 'records/UserCondition';
import ReviewerIsRecipientComponent from 'components/triggers/conditions/ReviewerIsRecipientComponent';

export const FeedbackTriggerType = Object.freeze({
  MATCH_ANY: 'matchAny',
  MATCH_ALL: 'matchAll',
  REVIEWER_IS_RECIPIENT: 'reviewer_is_recipient',
  RECIPIENT: 'recipient',
  COMMENT: 'comment',
  HIGHLIGHT: 'highlight',
  COACHES: 'coaches',
  SCORES: 'score',
  LISTENS: 'listens',
  SHARES: 'shares',
  ADDS_TO_LIBRARY: 'library_id',
  LIBRARY_ACCESS: 'library_access',
  LIBRARY_TAG: 'tag_name',

  getFeedbackTriggerContext(details) {
    if (!details || !details.type) {
      return null;
    }
    if (Iterable.isIterable(details)) {
      return details;
    }
    // eslint-disable-next-line
    switch (details.type) {
      case FeedbackTriggerType.COMMENT:
        return new FeedbackTriggerCommentContext(details);
      case FeedbackTriggerType.HIGHLIGHT:
        return new FeedbackTriggerHighlightContext(details);
      case FeedbackTriggerType.COACHES:
        return new FeedbackTriggerCoachContext(details);
      case FeedbackTriggerType.SHARES:
        return new FeedbackTriggerSharesContext(details);
      case FeedbackTriggerType.LISTENS:
        return new FeedbackTriggerListensContext(details);
      case FeedbackTriggerType.SCORES:
        return new FeedbackTriggerScoreContext(details);
      case FeedbackTriggerType.ADDS_TO_LIBRARY:
        return new FeedbackTriggerLibraryContext(details);
      case FeedbackTriggerType.LIBRARY_ACCESS:
        return new FeedbackTriggerLibraryAccessContext(details);
      case FeedbackTriggerType.LIBRARY_TAG:
        return new FeedbackTriggerLibraryTagContext(details);
      case FeedbackTriggerType.MATCH_ANY:
        return new FeedbackMatchAnyContext(details);
      case FeedbackTriggerType.MATCH_ALL:
        return new FeedbackMatchAllContext(details);
      case FeedbackTriggerType.RECIPIENT:
        return new FeedbackRecipientContext(details);
      case FeedbackTriggerType.REVIEWER_IS_RECIPIENT:
        return new FeedbackReviewerIsRecipient(details);
    }
  },
});

export class FeedbackMatchAnyContext extends Record({
  type: FeedbackTriggerType.MATCH_ANY,
  matchAny: List(),
}) {
  constructor(props = {matchAny: List()}) {
    const topMatchAny = Iterable.isIterable(props.matchAny) ? props.matchAny : List(props.matchAny);
    const matchAny = topMatchAny.map(c => FeedbackTriggerType.getFeedbackTriggerContext(c));
    super({
      ...props,
      matchAny,
    });
  }

  ready() {
    return this.matchAny.size > 0 && this.matchAny.every(context => context.ready());
  }
}

export class FeedbackMatchAllContext extends Record({
  type: FeedbackTriggerType.MATCH_ALL,
  matchAll: List(),
}) {
  constructor(props = {matchAll: List()}) {
    const topMatchAll = Iterable.isIterable(props.matchAll) ? props.matchAll : List(props.matchAll);
    const matchAll = topMatchAll.map(c => FeedbackTriggerType.getFeedbackTriggerContext(c));
    super({
      ...props,
      matchAll,
    });
  }

  ready() {
    return this.matchAll.size > 0 && this.matchAll.every(context => context.ready());
  }
}

export class FeedbackRecipientContext extends Record({
  type: FeedbackTriggerType.RECIPIENT,
  recipient: undefined,
  negated: false,
}) {
  constructor(props = {recipient: undefined}) {
    super({
      ...props,
      recipient: UserConditionType.ensureUserCondition(props.recipient),
    });
  }

  ready() {
    return !!this.recipient;
  }
}

export class FeedbackReviewerIsRecipient extends Record({
  type: FeedbackTriggerType.REVIEWER_IS_RECIPIENT,
  negated: false,
  component: ReviewerIsRecipientComponent,
}) {
  constructor(props) {
    super({
      ...props,
    });
  }

  getValue() {
    return this.negated;
  }
  ready() {
    return true;
  }
}

export class FeedbackTriggerCommentContext extends Record({
  type: FeedbackTriggerType.COMMENT,
  matching: List(),
  negated: false,
}) {
  constructor(props = {matching: []}) {
    super({
      ...props,
      matching: List(props.matching),
    });
  }

  ready() {
    return this.matching.size === 0 || this.matching.every(comment => !!comment);
  }
}

export class FeedbackTriggerHighlightContext extends Record({
  type: FeedbackTriggerType.HIGHLIGHT,
  matching: List(),
}) {
  constructor(props = {matching: []}) {
    super({
      ...props,
      matching: List(props.matching),
    });
  }

  ready() {
    return this.matching.size === 0 || this.matching.every(comment => !!comment);
  }
}

export class FeedbackTriggerCoachContext extends Record({
  type: FeedbackTriggerType.COACHES,
}) {
  ready() {
    return true;
  }
}

export class FeedbackTriggerScoreContext extends Record({
  type: FeedbackTriggerType.SCORES,
  range: undefined,
}) {
  ready() {
    return !this.range || this.range.end >= this.range.start;
  }
}

export class FeedbackTriggerListensContext extends Record({
  type: FeedbackTriggerType.LISTENS,
  range: undefined,
}) {
  ready() {
    return !this.range || this.range.end >= this.range.start;
  }
}

export class FeedbackTriggerSharesContext extends Record({
  type: FeedbackTriggerType.SHARES,
  matching: List(),
  negated: false,
}) {
  constructor(props = {matching: []}) {
    super({
      ...props,
      matching: List(props.matching),
    });
  }

  ready() {
    return true;
  }
}

export class FeedbackTriggerLibraryContext extends Record({
  type: FeedbackTriggerType.ADDS_TO_LIBRARY,
  library_id: null,
  negated: false,
}) {
  constructor(props = {}) {
    super({
      library_id: props.libraryId,
      ...props,
    });
  }

  ready() {
    return true;
  }

  getValue() {
    return this.library_id;
  }
}

export const LibraryAccessType = Object.freeze({
  LIBRARY_I_CREATED: 'LIBRARY_I_CREATED',
  LIBRARY_I_ADMIN: 'LIBRARY_I_ADMIN',
  LIBRARY_I_CAN_VIEW: 'LIBRARY_I_CAN_VIEW',
  ANY_LIBRARY: 'ANY_LIBRARY',
});

export class FeedbackTriggerLibraryAccessContext extends Record({
  type: FeedbackTriggerType.LIBRARY_ACCESS,
  matching: LibraryAccessType.ANY_LIBRARY,
  negated: false,
}) {
  ready() {
    return !!this.matching;
  }

  getValue() {
    return this.matching;
  }
}

export class FeedbackTriggerLibraryTagContext extends Record({
  type: FeedbackTriggerType.LIBRARY_TAG,
  tag_name: null,
  negated: false,
}) {
  constructor(props = {}) {
    super({
      tag_name: props.tagName,
      ...props,
    });
  }

  ready() {
    return !!this.tag_name;
  }

  getValue() {
    return this.tag_name;
  }
}

export default class FeedbackTrigger extends Record({
  type: 'feedback_trigger',
  user: List(),
  context: new FeedbackMatchAllContext(),
}) {
  constructor(props = {user: List(), context: new FeedbackMatchAllContext()}) {
    const userList = Iterable.isIterable(props.user) ? props.user : List(props.user);
    const userContext = userList.map(c =>
      Iterable.isIterable(c) ? c : UserConditionType.getUserConditionFromJson(c)
    );
    const context = FeedbackTriggerType.getFeedbackTriggerContext(props.context);
    super({
      ...props,
      user: userContext,
      context,
    });
  }

  getUserIds() {
    return this.user.filter(({type}) => type === 'user').map(({user_id}) => user_id);
  }

  getTeamIds() {
    return this.user.filter(({type}) => type === 'team').map(({team_id}) => team_id);
  }

  ready() {
    return (
      this.user.size > 0 && this.user.every(u => !u.ready || u.ready()) && this.context.ready()
    );
  }
}
